import { useAuthStore } from "@/kibi/modules/auth/domain/store/auth_store";
import * as Sentry from "@sentry/vue";
import { ofetch } from "ofetch";
import bus from "./eventBus";

let refreshPromise: Promise<void> | null = null;
const logout = {
  status: 401,
  error: "logout",
};
export const $api = ofetch.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "/api/v1",
  timeout: 30000, // 30 seconds
  retry: 1,
  retryStatusCodes: [401],
  async onRequest({ options }) {
    const authStore = useAuthStore();
    const accessToken = authStore.token?.access;
    options.headers = options.headers || {};
    if (options.headers instanceof Headers) {
      options.headers.set("Authorization", `Bearer ${accessToken}`);
    } else {
      (options.headers as any).Authorization = `Bearer ${accessToken}`;
    }
  },
  async onResponseError({ response, request, options }) {
    const authStore = useAuthStore();

    // Handle 401 Unauthorized - Token expired
    if (
      response?.status === 401 &&
      !(
        typeof request === "string" ? request : (request as any).url || ""
      ).includes("/auth/refresh-token")
    ) {
      // If we have a refresh token and haven't retried yet, try to refresh
      if (authStore.token?.refresh && !(options as any)._retry) {
        if (!refreshPromise) {
          refreshPromise = authStore
            .refreshToken()
            .catch((err) => {
              bus.emit("api_error", logout);
            })
            .finally(() => {
              refreshPromise = null;
            });
        }

        try {
          await refreshPromise;

          // Mark this request as retrying so we don't loop if it fails again
          (options as any)._retry = true;

          // After this hook finishes, ofetch will automatically retry the request
          // because 401 is in retryStatusCodes and retry count is 1.
          return;
        } catch (error) {
          // Refresh failed, logout
          bus.emit("api_error", logout);
          throw error;
        }
      } else {
        // No refresh token or already retried, logout
        bus.emit("api_error", logout);
      }
    }
    // When a forbidden/access defined error occur on the backend,
    // We trigger an event with the error type, App.vue listens and handles this event
    if (response?.status === 403) {
      Sentry.addBreadcrumb({
        category: "api",
        message: `403 Forbidden: ${typeof request === "string" ? request : ""}`,
        level: "warning",
        data: { error: response._data?.error },
      });
      bus.emit("api_error", {
        status: response.status,
        error: response._data?.error,
      });
    }
    // When a validation error occurs, it is emitted to pages/forms listening
    if (response?.status === 422) {
      Sentry.addBreadcrumb({
        category: "api",
        message: `422 Validation Error: ${typeof request === "string" ? request : ""}`,
        level: "info",
        data: response._data,
      });
      bus.emit("validation_error", response._data);
    }
    // Capture server errors (5xx) as exceptions
    if (response?.status && response.status >= 500) {
      Sentry.captureException(new Error(`API ${response.status}: ${typeof request === "string" ? request : ""}`), {
        extra: { responseData: response._data },
      });
    }
    return;
  },
});
